import { useState, useEffect } from "react";
import { Menu, X, Anchor, WifiOff, RefreshCw } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { CartButton } from "@/components/shop/CartButton";
import { NotificationBell } from "@/components/NotificationBell";
import { InstallButton } from "@/components/InstallButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useBackgroundSyncContext } from "@/components/BackgroundSyncProvider";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [yachtClubMember, setYachtClubMember] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const location = useLocation();
  const { syncNow } = useBackgroundSyncContext();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    checkYachtClubMembership();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkYachtClubMembership();
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkYachtClubMembership = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setYachtClubMember(null);
      return;
    }

    const { data } = await supabase
      .from("yacht_club_members")
      .select("membership_tier")
      .eq("user_id", user.id)
      .eq("status", "active")
      .maybeSingle();

    setYachtClubMember(data?.membership_tier || null);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      scrollToTop();
    }
  };

  const handleManualSync = async () => {
    if (!isOnline || isSyncing) return;
    setIsSyncing(true);
    try {
      await syncNow();
    } finally {
      setIsSyncing(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "sailor":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "captain":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "admiral":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default:
        return "bg-primary/20 text-primary";
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] bg-background/90 backdrop-blur-md border-b border-border safe-top safe-left safe-right">
      <div className="container mx-auto px-4 md:px-8 py-3 md:py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/" onClick={handleLogoClick} className="flex items-center tap-highlight-none">
            <h1 className="text-[24px] md:text-[44px] font-bold tracking-[0.05em] text-foreground uppercase">
              PARK ROW
            </h1>
          </Link>
          {!isOnline && (
            <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 text-xs gap-1">
              <WifiOff className="h-3 w-3" />
              <span className="hidden sm:inline">Offline</span>
            </Badge>
          )}
        </div>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex gap-8 items-center">
          <button onClick={() => scrollToSection("tour")} className="text-[14px] md:text-[15px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground transition-colors uppercase touch-target flex items-center justify-center">
            TOUR
          </button>
          <Link to="/yacht-club" className="text-[14px] md:text-[15px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground transition-colors uppercase touch-target flex items-center justify-center">
            YACHT CLUB
          </Link>
          <Link to="/shop" className="text-[14px] md:text-[15px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground transition-colors uppercase touch-target flex items-center justify-center">
            SHOP
          </Link>
          <button onClick={() => scrollToSection("band")} className="text-[14px] md:text-[15px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground transition-colors uppercase touch-target flex items-center justify-center">
            BAND
          </button>
          {yachtClubMember ? (
            <Link to="/yacht-club/dashboard" className="flex items-center gap-1">
              <Badge className={`${getTierColor(yachtClubMember)} capitalize`}>
                <Anchor className="h-3 w-3 mr-1" />
                {yachtClubMember}
              </Badge>
            </Link>
          ) : (
            <Link to="/fan-auth" className="text-[14px] md:text-[15px] font-semibold tracking-[0.03em] text-primary hover:text-primary/80 transition-colors uppercase touch-target flex items-center justify-center">
              LOGIN
            </Link>
          )}
          <NotificationBell />
          {isOnline && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleManualSync}
              disabled={isSyncing}
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              title="Sync cached data"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
            </Button>
          )}
          <InstallButton variant="compact" />
          <CartButton />
        </div>

        {/* Mobile Menu Button - iOS optimized touch target */}
        <div className="flex items-center gap-1 md:hidden">
          {yachtClubMember && (
            <Link to="/yacht-club/dashboard">
              <Badge className={`${getTierColor(yachtClubMember)} text-xs`}>
                <Anchor className="h-3 w-3" />
              </Badge>
            </Link>
          )}
          <NotificationBell />
          <InstallButton variant="compact" />
          <CartButton />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-foreground p-3 hover:bg-muted/50 active:bg-muted/70 rounded-lg transition-colors z-[101] touch-target tap-highlight-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown - iOS optimized */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background/98 backdrop-blur-lg border-t border-border safe-bottom">
          <div className="container mx-auto px-4 py-3 flex flex-col gap-1">
            <button onClick={() => scrollToSection("tour")} className="text-[16px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none">
              TOUR
            </button>
            <Link to="/yacht-club" onClick={() => setMobileMenuOpen(false)} className="text-[16px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none">
              YACHT CLUB
            </Link>
            <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="text-[16px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none">
              SHOP
            </Link>
            <button onClick={() => scrollToSection("band")} className="text-[16px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none">
              BAND
            </button>
            {yachtClubMember ? (
              <Link to="/yacht-club/dashboard" onClick={() => setMobileMenuOpen(false)} className="text-[16px] font-semibold tracking-[0.03em] text-primary hover:text-primary/80 active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none flex items-center gap-2">
                <Anchor className="h-4 w-4" />
                YACHT CLUB
              </Link>
            ) : (
              <Link to="/fan-auth" onClick={() => setMobileMenuOpen(false)} className="text-[16px] font-semibold tracking-[0.03em] text-primary hover:text-primary/80 active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none">
                LOGIN
              </Link>
            )}
            {isOnline && (
              <button
                onClick={handleManualSync}
                disabled={isSyncing}
                className="text-[16px] font-medium tracking-[0.03em] text-muted-foreground hover:text-foreground active:bg-muted/70 transition-colors uppercase text-left py-4 px-4 rounded-lg touch-target tap-highlight-none flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
                {isSyncing ? "SYNCING..." : "SYNC DATA"}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;